import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { toastManager } from "../components/ui";
import { SettingsHeader } from "../components/SettingsHeader";
import {
  DOWNLOAD_FORMATS,
  usePreferences,
  useUpdatePreferences,
  type DownloadFormat,
} from "../lib/usePreferences";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/settings/downloads")({
  component: DownloadSettings,
});

const USES: Record<DownloadFormat, string> = {
  svg: "Sharp at any size. Best for websites, print and editing in a design tool.",
  png_hires:
    "A large transparent image. Best for slides, documents and social posts.",
  webp: "A small transparent image. Best when page speed matters.",
};

function DownloadSettings() {
  const prefs = usePreferences();
  const update = useUpdatePreferences();
  const current = update.isPending
    ? update.variables?.download_format
    : prefs.data?.download_format;

  function choose(format: DownloadFormat) {
    if (format === current) return;
    update.mutate(
      { download_format: format },
      {
        onError: () =>
          toastManager.add({
            type: "error",
            title: "Could not save your download format",
          }),
      }
    );
  }

  return (
    <div>
      <SettingsHeader title="Downloads">
        Pick the format you use most. It is listed first in every download menu,
        and "Download pack" zips your variations in it.
      </SettingsHeader>

      <section className="tactile p-4 sm:p-6">
        <h3 id="download-format" className="text-base text-ink">
          Default format
        </h3>
        <div
          role="radiogroup"
          aria-labelledby="download-format"
          className="mt-4 grid gap-3 sm:grid-cols-3"
        >
          {DOWNLOAD_FORMATS.map((f) => {
            const selected = current === f.kind;
            return (
              <button
                key={f.kind}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={prefs.isLoading}
                onClick={() => choose(f.kind)}
                className={cn(
                  "relative flex flex-col items-start rounded-2xl border-2 p-4 text-left transition-colors disabled:opacity-60",
                  selected
                    ? "border-lilac-600 bg-lilac-100"
                    : "border-line bg-surface hover:border-line-strong"
                )}
              >
                {selected && (
                  <Check
                    className="absolute right-3 top-3 size-4 text-lilac-600"
                    aria-hidden="true"
                  />
                )}
                <span className="font-display text-xl font-semibold text-ink">
                  {f.label}
                </span>
                <span className="text-xs font-extrabold text-ink-faint">
                  {f.hint}
                </span>
                <span className="mt-2 text-xs font-semibold text-ink-soft">
                  {USES[f.kind]}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-4 text-xs font-semibold text-ink-faint">
          Every format stays available from the download menu. If a mascot has
          no file in your format yet, the pack uses its SVG instead.
        </p>
      </section>
    </div>
  );
}
